import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router';
import { ApiError } from './lib/api';
import { SessionProvider } from './lib/session';
import { RedirectIfSignedIn, RequireSession } from './lib/guards';
import { ThemeProvider } from './lib/theme';
import { ToastProvider } from './components/toast';
import { ConfirmProvider } from './components/dialog';
import { Spinner } from './components/ui';
import { LeaveGuardProvider } from './lib/leave-guard';
import { AppShell } from './routes/shell';
import { LoginRoute } from './routes/login';
import { DashboardRoute } from './routes/dashboard';
import { TicketsRoute } from './routes/tickets';
import { TicketDetailRoute } from './routes/ticket-detail';
import { NewTicketRoute } from './routes/new-ticket';
import { NotFoundRoute } from './routes/not-found';
import './styles/app.css';

/**
 * A page whose code loads when it is first opened.
 *
 * The whole application used to arrive in one 1.35 MB script before the sign-in
 * page could draw. What an agent opens all day — the shell, sign-in, the
 * dashboard, the ticket list and a ticket — stays in that first script;
 * administration, reports, people, the catalogue and the practice records
 * load on demand, with the shell already on screen while they do.
 */
function page<M>(load: () => Promise<M>, name: keyof M) {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

const ProblemsRoute = page(() => import('./routes/modules'), 'ProblemsRoute');
const ChangesRoute = page(() => import('./routes/modules'), 'ChangesRoute');
const ReleasesRoute = page(() => import('./routes/modules'), 'ReleasesRoute');
const AssetsRoute = page(() => import('./routes/modules'), 'AssetsRoute');
const KnowledgeRoute = page(() => import('./routes/modules'), 'KnowledgeRoute');
const ProblemDetailRoute = page(() => import('./routes/module-screens'), 'ProblemDetailRoute');
const NewProblemRoute = page(() => import('./routes/module-screens'), 'NewProblemRoute');
const ChangeDetailRoute = page(() => import('./routes/module-screens'), 'ChangeDetailRoute');
const NewChangeRoute = page(() => import('./routes/module-screens'), 'NewChangeRoute');
const ReleaseDetailRoute = page(() => import('./routes/module-screens'), 'ReleaseDetailRoute');
const NewReleaseRoute = page(() => import('./routes/module-screens'), 'NewReleaseRoute');
const AssetDetailRoute = page(() => import('./routes/module-screens'), 'AssetDetailRoute');
const NewAssetRoute = page(() => import('./routes/module-screens'), 'NewAssetRoute');
const ArticleDetailRoute = page(() => import('./routes/module-screens'), 'ArticleDetailRoute');
const NewArticleRoute = page(() => import('./routes/module-screens'), 'NewArticleRoute');
const ApprovalsRoute = page(() => import('./routes/approvals'), 'ApprovalsRoute');
const ReportsRoute = page(() => import('./routes/reports'), 'ReportsRoute');
const AdminRoute = page(() => import('./routes/admin'), 'AdminRoute');
const CatalogRoute = page(() => import('./routes/catalog'), 'CatalogRoute');
const CatalogItemRoute = page(() => import('./routes/catalog'), 'CatalogItemRoute');
const PeopleRoute = page(() => import('./routes/people'), 'PeopleRoute');
const VerifyRoute = page(() => import('./routes/verify'), 'VerifyRoute');
const ChangePasswordRoute = page(() => import('./routes/change-password'), 'ChangePasswordRoute');
const SecurityRoute = page(() => import('./routes/security'), 'SecurityRoute');
const SoftwareListRoute = page(() => import('./features/software/software-list'), 'SoftwareListRoute');
const SoftwareDetailRoute = page(() => import('./features/software/software-detail'), 'SoftwareDetailRoute');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data an agent is looking at goes stale quickly, but refetching on every
      // mount makes navigation feel jumpy. Fifteen seconds is the compromise,
      // with focus refetching to catch up after a context switch.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry a rejection the server meant: a 4xx will fail again
        // identically and only delays showing the user what went wrong.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/**
 * What every page sits inside. On a route rather than around the router, so
 * the providers can use the router — the leave guard's blocker needs it.
 */
function Root() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <LeaveGuardProvider>
          <SessionProvider>
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Spinner label="Loading" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </SessionProvider>
        </LeaveGuardProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

/**
 * A data router rather than a declarative one, because only a data router can
 * block navigation — which is what lets unsaved work survive the command
 * palette and the browser's Back button (see lib/leave-guard.tsx).
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />}>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <LoginRoute />
          </RedirectIfSignedIn>
        }
      />

      {/* Both of these are reached with a half-authenticated session, so they
          sit outside RequireSession — which would bounce them straight back to
          the sign-in page they just came from. The second step does sit behind
          RedirectIfSignedIn: the moment the code is accepted the session lands,
          and that guard is what takes the person on to the page they wanted. */}
      <Route
        path="/verify"
        element={
          <RedirectIfSignedIn>
            <VerifyRoute />
          </RedirectIfSignedIn>
        }
      />
      <Route path="/change-password" element={<ChangePasswordRoute />} />

      <Route
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route index element={<DashboardRoute />} />
        <Route path="tickets" element={<TicketsRoute />} />
        <Route path="tickets/new" element={<NewTicketRoute />} />
        <Route path="tickets/:number" element={<TicketDetailRoute />} />
        {/* `new` before `:number` in each pair: the parameterised route would
            otherwise match the literal word "new" and try to load a record
            called that. */}
        <Route path="problems" element={<ProblemsRoute />} />
        <Route path="problems/new" element={<NewProblemRoute />} />
        <Route path="problems/:number" element={<ProblemDetailRoute />} />
        <Route path="changes" element={<ChangesRoute />} />
        <Route path="changes/new" element={<NewChangeRoute />} />
        <Route path="changes/:number" element={<ChangeDetailRoute />} />
        <Route path="releases" element={<ReleasesRoute />} />
        <Route path="releases/new" element={<NewReleaseRoute />} />
        <Route path="releases/:number" element={<ReleaseDetailRoute />} />
        <Route path="assets" element={<AssetsRoute />} />
        <Route path="assets/new" element={<NewAssetRoute />} />
        <Route path="assets/:number" element={<AssetDetailRoute />} />
        <Route path="software" element={<SoftwareListRoute />} />
        <Route path="software/:id" element={<SoftwareDetailRoute />} />
        <Route path="knowledge" element={<KnowledgeRoute />} />
        <Route path="knowledge/new" element={<NewArticleRoute />} />
        <Route path="knowledge/:id" element={<ArticleDetailRoute />} />
        <Route path="approvals" element={<ApprovalsRoute />} />
        <Route path="catalog" element={<CatalogRoute />} />
        <Route path="catalog/:id" element={<CatalogItemRoute />} />
        <Route path="reports" element={<ReportsRoute />} />
        <Route path="security" element={<SecurityRoute />} />
        <Route path="admin/people" element={<PeopleRoute />} />
        <Route path="admin/*" element={<AdminRoute />} />
        {/* Inside the shell, and so behind sign-in: an unknown address says
            so, with the navigation and search to hand. It used to redirect to
            the dashboard without a word. A signed-out visitor signs in first
            and comes back to this answer. */}
        <Route path="*" element={<NotFoundRoute />} />
      </Route>
    </Route>,
  ),
);

const container = document.getElementById('root');
if (!container) throw new Error('Root element is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
